// src/design-system/Molecules/ContactANurseModal/index.tsx
import React, { useState, useEffect } from "react";
import { Modal } from "@/design-system/Atoms/Modal";
import { Button, FloatingLabelInput } from "@/design-system/Atoms";
import { TextAreaField } from "@/design-system/Atoms/TextArea";
import {
  contactNurseFormSchema,
  type ContactNurseFormSchema,
} from "@/validation-schemas/contact-nurse-schemas";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useContactNurseService } from "@/rtkQuery/features/hooks/useContactNurseService";
import { useInViewOnce } from "@/hooks/useInViewOnce";

interface ContactNurseModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: number;
  productName: string;
}

export const ContactNurseModal: React.FC<ContactNurseModalProps> = ({
  isOpen,
  onClose,
  orderId = 0,
  productName = "",
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactNurseFormSchema>({
    resolver: zodResolver(contactNurseFormSchema),
    defaultValues: {
      orderId: orderId,
      message: "",
    },
    mode: "onTouched",
  });
  useEffect(() => {
    if (isOpen && orderId > 0) {
      reset({
        orderId,
        message: "",
      });
    }
  }, [isOpen, orderId, reset]);

  const { submitNurseRequest, contactANurseErrorMessage } =
    useContactNurseService();
  const [responseMessage, setResponseMessage] = useState<string | null>(null);
  const [responseFlag, setResponseFlag] = useState<boolean>(false);
  const { ref } = useInViewOnce<HTMLDivElement>();

  const onSubmit = async (data: ContactNurseFormSchema) => {
    const result = await submitNurseRequest({
      orderId: data.orderId,
      message: data.message,
    });

    if (result && result.Result.toLowerCase() === "success") {
      reset();
      setResponseFlag(true);
      setResponseMessage(
        result.Message || "Nurse request submitted successfully."
      );
      setTimeout(() => {
        setResponseMessage(null);
        onClose();
      }, 5000);
    } else {
      setResponseMessage(result?.Message || contactANurseErrorMessage);
      setResponseFlag(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        reset();
        setResponseMessage(null);
        onClose();
      }}
      title="We’re here to help!"
      size="xl"
      className="max-h-screen max-w-[42rem]"
      childrenClassName="max-h-[calc(100vh-120px)]"
    >
      <div className="p-6">
        <div className="prose prose-sm max-w-none">
          <div className="bg-[#f6f7f9] p-6 lg:w-full mx-auto rounded-[20px]">
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <input
                type="hidden"
                {...register("orderId", { valueAsNumber: true })}
                readOnly
              />
              {errors.orderId?.message && (
                <div className="text-sm text-red-600 font-medium mb-4">
                  {errors.orderId.message}
                </div>
              )}
              <div className="w-full mb-4">
                <FloatingLabelInput
                  id="productName"
                  label="Product Name"
                  defaultValue={productName}
                  type="text"
                  readOnly
                />
              </div>

              <div className="mb-6">
                <TextAreaField
                  placeholder="Please let us know your question..."
                  rows={3}
                  {...register("message")}
                  error={errors.message?.message}
                />
              </div>
              {(responseMessage || errors.orderId?.message) && (
                <div
                  ref={ref}
                  className={`animate-fade-in-up mt-6 mb-6 p-4 rounded-md text-sm font-bold border ${
                    responseMessage && responseFlag
                      ? "border-green-300 bg-green-50 text-green-700"
                      : "border-red-300 bg-red-50 text-red-700"
                  }`}
                  dangerouslySetInnerHTML={{
                    __html: responseMessage ?? errors.orderId?.message ?? "",
                  }}
                />
              )}
              <Button
                variant="secondary"
                size="xl"
                className="!w-full"
                type="submit"
                disabled={isSubmitting}
                loading={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Contact a Nurse"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ContactNurseModal;
